// Composant affichage du Casque - V7 (1-5 casques + badge dore MASE)

import { SEUILS_CASQUES, type Casque, type SeuilCasque } from '@autodiag/shared'
import { cn } from '../../lib/utils'

export type { Casque }

export interface CasqueDisplayProps {
  casque: Casque
  scoreGlobal: number
  maxScore?: number
  showDetails?: boolean
}

// V7: Configuration pour chaque niveau de casques
const CASQUE_CONFIG: Record<Casque, {
  label: string
  description: string
}> = {
  1: {
    label: '1 Casque',
    description: 'Début du parcours. Des actions concrètes vous permettront de progresser.',
  },
  2: {
    label: '2 Casques',
    description: 'Premiers pas en prévention. Continuez sur cette lancée !',
  },
  3: {
    label: '3 Casques',
    description: 'Bonne progression. Vous êtes sur la bonne voie.',
  },
  4: {
    label: '4 Casques',
    description: 'Bon niveau. Votre engagement porte ses fruits.',
  },
  5: {
    label: '5 Casques',
    description: 'Excellence ! Votre engagement en prévention est exemplaire.',
  },
}

export default function CasqueDisplay({
  casque,
  scoreGlobal,
  maxScore,
  showDetails = true,
}: CasqueDisplayProps) {
  const config = CASQUE_CONFIG[casque]
  const percentage = maxScore ? Math.round((scoreGlobal / maxScore) * 100) : scoreGlobal

  return (
    <div className="rounded-xl border-2 p-6 bg-primary/5 border-primary/30">
      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          <CasqueIcons count={casque} size="lg" />
        </div>

        <div className="flex-1">
          <h3 className="text-xl font-bold text-primary">
            {config.label}
          </h3>

          <div className="text-3xl font-bold text-primary mt-1">
            {scoreGlobal}{maxScore ? ` / ${maxScore}` : ''} pts
          </div>

          {showDetails && (
            <p className="text-sm mt-2 text-primary/80">
              {config.description}
            </p>
          )}
        </div>
      </div>

      {/* Barre de progression — 5 segments */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span>1</span>
          <span>2</span>
          <span>3</span>
          <span>4</span>
          <span>5</span>
        </div>
        <div className="relative h-3 bg-muted rounded-full overflow-hidden">
          <div className="absolute inset-0 flex">
            <div className="w-[20%] bg-primary/20" />
            <div className="w-[20%] bg-primary/40" />
            <div className="w-[20%] bg-primary/60" />
            <div className="w-[20%] bg-primary/80" />
            <div className="w-[20%] bg-primary" />
          </div>

          <div
            className="absolute top-0 bottom-0 w-1 -ml-0.5 bg-foreground"
            style={{ left: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// V7: Affichage des casques (remplis et vides) — 5 max
export function CasqueIcons({
  count,
  size = 'md',
  maxCasques = 5,
}: {
  count: Casque | number
  size?: 'sm' | 'md' | 'lg'
  maxCasques?: number
}) {
  const sizeClasses = {
    sm: 'w-6 h-6 sm:w-7 sm:h-7',
    md: 'w-8 h-8 sm:w-10 sm:h-10',
    lg: 'w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14',
  }

  const gap = {
    sm: 'gap-1',
    md: 'gap-2',
    lg: 'gap-3',
  }

  return (
    <div className={`flex ${gap[size]}`}>
      {Array.from({ length: maxCasques }).map((_, i) => {
        const isActive = i < count
        return (
          <img
            key={i}
            src={isActive ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
            alt={isActive ? 'Casque actif' : 'Casque inactif'}
            className={sizeClasses[size]}
          />
        )
      })}
    </div>
  )
}

// Icone du casque individuel
export function CasqueIcon({
  casque,
  size = 'md',
}: {
  casque: Casque
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizeClasses = {
    sm: 'w-8 h-8 text-lg',
    md: 'w-12 h-12 text-2xl',
    lg: 'w-16 h-16 text-4xl',
  }

  return (
    <img
      src="/images/casque-actif.svg"
      alt={`Casque niveau ${casque}`}
      className={sizeClasses[size]}
    />
  )
}

// Composant compact pour les listes
export function CasqueBadge({ casque }: { casque: Casque }) {
  const config = CASQUE_CONFIG[casque]

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
      <CasqueIcons count={casque} size="sm" maxCasques={casque} />
      <span>{config.label}</span>
    </span>
  )
}

// V7: Explications du calcul des casques — 5 niveaux + badge dore
interface CasqueExplanationProps {
  nombreMetiers?: number
}

export function CasqueExplanation({ nombreMetiers = 1 }: CasqueExplanationProps) {
  const nbMetiers = Math.max(1, Math.min(3, nombreMetiers)) as 1 | 2 | 3
  const seuils: SeuilCasque[] = SEUILS_CASQUES[nbMetiers]

  return (
    <div className="text-sm">
      <p className="text-muted-foreground mb-4">
        Avec <span className="font-bold text-foreground">{nbMetiers} métier{nbMetiers > 1 ? 's' : ''}</span> évalué{nbMetiers > 1 ? 's' : ''}, voici les seuils :
      </p>

      <div className="space-y-2">
        {seuils.map((seuil, index) => {
          const level = index + 1
          const range = seuil.max === Infinity
            ? `> ${seuil.min} pts`
            : `${seuil.min}-${seuil.max} pts`

          return (
            <div key={level} className="flex items-center gap-3 py-2 px-3 bg-primary/5 rounded-lg">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                level <= 2 ? 'bg-primary/20 text-primary' : level <= 4 ? 'bg-primary/60 text-primary-foreground' : 'bg-primary text-primary-foreground'
              )}>
                <span className="font-bold text-sm">{level}</span>
              </div>
              <div className="flex-1 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <img
                      key={i}
                      src={i < level ? '/images/casque-actif.svg' : '/images/casque-desactive.svg'}
                      alt=""
                      className="w-5 h-5"
                      aria-hidden="true"
                    />
                  ))}
                </div>
                <div className={cn(
                  'text-right',
                  level >= 4 ? 'text-primary font-medium' : 'text-muted-foreground font-medium'
                )}>
                  <span>{range}</span>
                  {/* US-29 : mention CPP à côté du seuil du 5e casque */}
                  {level === 5 && (
                    <span className="block text-[11px] text-muted-foreground italic font-normal mt-0.5">
                      Assujetti à un CPP
                    </span>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        {/* Badge dore MASE — medaillon */}
        <div className="flex items-center gap-3 py-2 px-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/50 mt-2">
          <img src="/images/medaille-dore.svg" alt="Médaille dorée" className="w-8 h-8 flex-shrink-0" />
          <div className="flex-1 flex items-center justify-between">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <img key={i} src="/images/casque-actif.svg" alt="" className="w-5 h-5" aria-hidden="true" />
              ))}
              <img src="/images/medaille-dore.svg" alt="" className="w-5 h-5" aria-hidden="true" />
            </div>
            <span className="text-amber-600 font-medium">+ certif.</span>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Le badge doré nécessite le même score que 5 casques + une certification MASE ou ISO 45001.
      </p>
    </div>
  )
}
